import { requestAuthAdmin } from './auth';
import { getSelectedEnvironment } from '../utils/apiEnvironment';
import { DM_COUNTING_SCOPE } from '../utils/dmUsage';

export const DM_ADMIN_CONTRACT = {
  sourceSha: '919dd200a53591aae2ecdb6b99912d71ac147c6c',
  specSha256: '0ab35579fdbf32ecfb960ed09ae9e731c909068275ce94809ada215da4388019',
  lineage: 'merged',
};
export const DM_ADMIN_PATH = '/api-admin/v1/moderation/dm';
export const DM_MODERATION_PURPOSE = 'DM_ADMIN_REVIEW';
export const DM_DURATION_SECONDS = { '10m': 600, '1h': 3600, '24h': 86400 };
export const DM_RESTRICTION_DAY_PRESETS = [1, 3, 7, 30, 90];
export const validDmRestrictionDays = days => Number.isInteger(days) && days >= 1 && days <= 90;
export const dmRestrictionSeconds = input => input.durationDays !== undefined ? (validDmRestrictionDays(input.durationDays) ? input.durationDays * 86400 : undefined) : DM_DURATION_SECONDS[input.duration];
export const REPORT_ACTION_PATHS = { review: 'start-review', dismiss: 'dismiss', violation: 'confirm-violation' };

export function contractError() {
  return Object.assign(new Error('DM Admin 응답이 candidate 계약과 다릅니다. 내역·조치 결과를 확인할 수 없습니다.'), { code: 'DM_ADMIN_CONTRACT_INVALID' });
}
const integer = (value) => Number.isSafeInteger(value) && value >= 0;
const text = (value) => typeof value === 'string';
const timestamp = (value) => text(value) && Number.isFinite(Date.parse(value));
const nullableTime = (value) => value === null || timestamp(value);
const requireValue = (condition) => { if (!condition) throw contractError(); };

function targetId(value) {
  requireValue(/^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value)));
  return String(value);
}

export function mapMessage(raw) {
  requireValue(raw && ['messageId', 'conversationId', 'body', 'status'].every((key) => text(raw[key])) && ['senderId', 'recipientId', 'seq', 'reportCount', 'violationCount'].every((key) => integer(raw[key])) && timestamp(raw.createdAt));
  return { id: raw.messageId, conversationId: raw.conversationId, senderId: raw.senderId, recipientId: raw.recipientId, text: raw.body, status: raw.status, createdAt: raw.createdAt, reportCount: raw.reportCount, violationCount: raw.violationCount };
}

export function mapReport(raw, context = []) {
  requireValue(raw && ['reportId', 'reporterId', 'senderId', 'reasonId', 'revision'].every((key) => integer(raw[key])) && (raw.recipientId === null || integer(raw.recipientId)) && nullableTime(raw.messageCreatedAt) && ['messageId', 'conversationId', 'body'].every((key) => text(raw[key])) && ['pending', 'review', 'dismissed', 'violation'].includes(raw.status) && timestamp(raw.createdAt) && timestamp(raw.updatedAt));
  const message = context.find((entry) => entry.id === raw.messageId);
  return {
    id: raw.reportId, revision: raw.revision, status: raw.status,
    reason: `신고 사유 #${raw.reasonId}`, reporterId: raw.reporterId, reportedAt: raw.createdAt,
    message: { ...message, id: raw.messageId, conversationId: raw.conversationId, senderId: raw.senderId, recipientId: raw.recipientId, createdAt: raw.messageCreatedAt, text: raw.body },
    // These transitions mirror the pinned provider; the server still decides.
    allowedActions: raw.status === 'pending' ? ['review', 'dismiss', 'violation'] : raw.status === 'review' ? ['dismiss', 'violation'] : [],
  };
}

export function mapAction(raw) {
  requireValue(raw && integer(raw.actionId) && integer(raw.adminUserId) && timestamp(raw.createdAt) && (raw.sourceReportId === null || integer(raw.sourceReportId)) && ['operationId', 'targetType', 'targetId', 'action', 'reason'].every((key) => text(raw[key])));
  const beforeRestriction = raw.beforeRestriction === null ? null : mapRestriction(raw.beforeRestriction);
  const afterRestriction = raw.afterRestriction === null ? null : mapRestriction(raw.afterRestriction);
  requireValue([beforeRestriction, afterRestriction].every((state) => state === null || (raw.targetType === 'restriction' && String(state.userId) === raw.targetId)));
  return { auditId: raw.actionId, action: raw.action, targetType: raw.targetType, targetId: raw.targetId, reasonCode: raw.reason, moderatorId: raw.adminUserId, createdAt: raw.createdAt, sourceReportId: raw.sourceReportId, beforeRestriction, afterRestriction };
}

export function mapUserUsage(raw) {
  requireValue(raw && ['dailyUsed', 'weeklyUsed', 'lifetimeUsed', 'reportCount', 'violationCount'].every((key) => integer(raw[key])) && raw.countingScope === DM_COUNTING_SCOPE && nullableTime(raw.countedSince) && timestamp(raw.snapshotAt));
  const monthly = Object.prototype.hasOwnProperty.call(raw, 'monthlyUsed') || Object.prototype.hasOwnProperty.call(raw, 'monthlyResetAt');
  if (monthly) requireValue(integer(raw.monthlyUsed) && timestamp(raw.monthlyResetAt));
  return { ...(monthly ? { monthly: raw.monthlyUsed, monthlyResetAt: raw.monthlyResetAt } : {}), daily: raw.dailyUsed, weekly: raw.weeklyUsed, sent: raw.lifetimeUsed, reports: raw.reportCount, violations: raw.violationCount, countedSince: raw.countedSince, snapshotAt: raw.snapshotAt, countingScope: raw.countingScope };
}

export function mapRestriction(raw) {
  requireValue(raw && integer(raw.userId) && integer(raw.revision) && integer(raw.adminUserId) && text(raw.reason) && typeof raw.active === 'boolean' && timestamp(raw.updatedAt) && nullableTime(raw.expiresAt) && nullableTime(raw.liftedAt));
  return { userId: raw.userId, revision: raw.revision, active: raw.active, expiresAt: raw.expiresAt, liftedAt: raw.liftedAt, updatedAt: raw.updatedAt, moderatorId: raw.adminUserId, reasonCode: raw.reason };
}

function mapPage(raw, map) {
  requireValue(raw && Array.isArray(raw.items) && typeof raw.hasMore === 'boolean' && (raw.nextCursor === null || (text(raw.nextCursor) && !!raw.nextCursor)) && (!raw.hasMore || !!raw.nextCursor));
  return { items: raw.items.map((item) => map(item)), nextCursor: raw.nextCursor, hasMore: raw.hasMore };
}

function listQuery(input, extra = {}) {
  const limit = input.limit ?? 50;
  requireValue(Number.isInteger(limit) && limit >= 1 && limit <= 100);
  const params = new URLSearchParams({ limit: String(limit) });
  Object.entries(extra).forEach(([key, value]) => { if (value != null && value !== '') params.set(key, String(value)); });
  return params.toString();
}

async function parse(response) {
  let body;
  try { body = JSON.parse(await response.text()); } catch { body = null; }
  // Never use auth.parseResponse: plaintext and audit reasons must not be logged.
  if (!response.ok) {
    const message = response.status === 404 || response.status === 503 ? 'DM Admin API가 미제공·비활성 상태이거나 조회 대상이 없습니다. 실제 TEST 배포 상태를 확인하세요.' : body?.msg || `DM Admin 요청 실패 (${response.status})`;
    throw Object.assign(new Error(message), { status: response.status, code: body?.error?.errorCode ?? body?.code, currentRevision: body?.error?.currentRevision });
  }
  requireValue(body && Object.prototype.hasOwnProperty.call(body, 'data') && body.data != null);
  return body.data;
}

export function createDmAdminProvider(transport = requestAuthAdmin) {
  const request = async (input, suffix, method = 'GET', body, purpose = false) => {
    if (!input.token) throw Object.assign(new Error('Admin 인증이 필요합니다.'), { status: 401 });
    if (!['test', 'prod'].includes(input.environment) || input.environment !== getSelectedEnvironment()) throw new Error('조회·조치 대상 환경이 변경되었습니다. 현재 환경에서 다시 조회하세요.');
    const options = { method, headers: purpose ? { 'X-Moderation-Purpose': DM_MODERATION_PURPOSE } : {} };
    if (body !== undefined) options.body = JSON.stringify(body);
    return parse(await transport(`${DM_ADMIN_PATH}/${suffix}`, options, input.token));
  };
  const restriction = async (input) => {
    const value = mapRestriction(await request(input, `users/${targetId(input.userId)}/restriction`));
    requireValue(String(value.userId) === String(input.userId));
    return value;
  };
  return {
    getRestriction: restriction,
    async listMessages(input) {
      const query = listQuery(input, { senderId: input.senderId ? targetId(input.senderId) : undefined, cursor: input.cursor });
      return mapPage(await request(input, `messages?${query}`, 'GET', undefined, true), mapMessage);
    },
    async listReports(input) {
      if (input.status) requireValue(['pending', 'review', 'dismissed', 'violation'].includes(input.status));
      const query = listQuery(input, { senderId: input.senderId ? targetId(input.senderId) : undefined, status: input.status, cursor: input.cursor });
      return mapPage(await request(input, `reports?${query}`, 'GET', undefined, true), mapReport);
    },
    async getCase(input) {
      const raw = await request(input, `reports/${targetId(input.caseId)}`, 'GET', undefined, true);
      requireValue(Array.isArray(raw.context) && raw.context.length <= 21 && Array.isArray(raw.actions) && raw.actions.length <= 50);
      const context = raw.context.map(mapMessage);
      const report = mapReport(raw.report, context);
      return { case: report, context, actions: raw.actions.map(mapAction), restriction: await restriction({ ...input, userId: report.message.senderId }) };
    },
    async getUserHistory(input) {
      const query = listQuery(input, { messageCursor: input.cursor, reportCursor: input.reportCursor, actionCursor: input.actionCursor });
      const raw = await request(input, `users/${targetId(input.userId)}?${query}`, 'GET', undefined, true);
      requireValue(raw.account && integer(raw.account.userId) && integer(raw.account.accountStatus) && (raw.account.accountInactiveReason === null || integer(raw.account.accountInactiveReason)));
      const messages = mapPage(raw.sentMessages, mapMessage);
      requireValue(String(raw.account.userId) === String(input.userId) && messages.items.every((message) => String(message.senderId) === String(input.userId)) && String(raw.restriction?.userId) === String(input.userId));
      const reports = mapPage(raw.reports, mapReport);
      const actions = mapPage(raw.actions, mapAction);
      requireValue(reports.items.every((report) => String(report.message.senderId) === String(input.userId)) && actions.items.every((action) => action.targetType !== 'restriction' || action.targetId === String(input.userId)));
      return { ...messages, reportPage: reports, actionPage: actions, restriction: mapRestriction(raw.restriction), account: raw.account, summary: mapUserUsage(raw.usage) };
    },
    async applyAction(input) {
      requireValue(integer(input.expectedRevision) && text(input.requestId) && /^[A-Za-z0-9_:-]{1,64}$/.test(input.requestId));
      const reason = [String(input.reasonCode || '').trim(), String(input.notes || '').trim()].filter(Boolean).join('\n');
      requireValue(reason.length >= 1 && reason.length <= 500);
      const body = { expectedRevision: input.expectedRevision, operationId: input.requestId, reason };
      let suffix;
      const reportAction = REPORT_ACTION_PATHS[input.action];
      if (reportAction) suffix = `reports/${targetId(input.caseId)}/${reportAction}`;
      else if (input.action === 'restrict' && dmRestrictionSeconds(input)) {
        suffix = `users/${targetId(input.userId)}/restriction`;
        body.durationSeconds = dmRestrictionSeconds(input);
        body.sourceReportId = input.sourceReportId == null ? null : Number(targetId(input.sourceReportId));
      }
      else if (input.action === 'lift') suffix = `users/${targetId(input.userId)}/restriction/lift`;
      else throw contractError();
      const raw = await request(input, suffix, 'POST', body);
      requireValue(integer(raw.auditId) && raw.auditId > 0);
      if (reportAction) {
        requireValue(raw.state && String(raw.state.reportId) === String(input.caseId) && integer(raw.state.revision) && raw.state.revision >= input.expectedRevision && ['pending', 'review', 'dismissed', 'violation'].includes(raw.state.status));
        return raw;
      }
      const state = mapRestriction(raw.state);
      requireValue(String(state.userId) === String(input.userId) && state.revision >= input.expectedRevision);
      return { auditId: raw.auditId, state };
    },
  };
}
